<template>
  <div :class="{ 'has-margin-bottom-75': !$_.get(field, 'is_array') }">
    <date-picker
      v-if="isDateTime"
      :key="`provision-field-${field.id}`"
      v-model="formValue"
      :field-vname="getFieldName"
      :field-name="field.name"
      :field-vid="fieldId"
      :disabled="isDisabled"
      :show-label="false"
      :is-required="field.required || isRequiredIf"
    />
    <template v-if="isSmartDomainField">
      <smart-domain-field
        v-model="formValue"
        v-bind="$attrs"
        :brand-id="brand.id"
        :field="field"
        :is-disabled="isDisabled"
        :label="showLabel ? getLabel : ''"
        :rules="rules"
        :vid="fieldId"
        :vname="getFieldName"
      />
    </template>
    <u-field
      v-else
      :key="`provision-field-${field.id}`"
      :label="showLabel && !isCheckbox ? getLabel : ''"
      :vname="getFieldName"
      :vid="fieldId"
      :rules="rules"
    >
      <b-field
        expanded
        class="is-flex has-margin-bottom-0"
        :type="{ 'is-danger': hasError(fieldId) }"
      >
        <b-autocomplete
          v-if="hasSuggestions"
          ref="focusedField"
          v-model.number="formValue"
          :data="data"
          :placeholder="
            noPlaceholder ? '-' : $t('_placeholder.enter_field_value')
          "
          :loading="isFetching"
          :open-on-focus="true"
          :clearable="true"
          :disabled="isDisabled"
          class="has-flex-grow"
          @typing="getAsyncData"
          @focus="getAsyncData"
          @select="option => (selected = option)"
        />
        <template v-else>
          <b-input
            v-if="isTextInput"
            ref="focusedField"
            v-model="formValue"
            :autocomplete="$uuid()"
            :disabled="isDisabled"
            :placeholder="
              noPlaceholder ? '-' : $t('_placeholder.enter_field_value')
            "
            class="has-flex-grow"
            type="text"
            expanded
          />
          <phone-number-field
            v-if="isTelInput"
            ref="focusedField"
            v-model="formValue"
            :is-validated="false"
            :is-disabled="isDisabled"
            class="has-flex-grow"
          />
          <b-input
            v-if="isPasswordInput"
            ref="focusedField"
            v-model="formValue"
            autocomplete="new-password"
            :disabled="isDisabled"
            :placeholder="noPlaceholder ? '-' : $t('_placeholder.password')"
            class="has-flex-grow"
            type="password"
            expanded
          />
          <b-input
            v-if="isTextarea"
            ref="focusedField"
            v-model="formValue"
            :autocomplete="$uuid()"
            :disabled="isDisabled"
            :placeholder="
              noPlaceholder ? '-' : $t('_placeholder.enter_field_value')
            "
            rows="4"
            class="has-flex-grow"
            type="textarea"
            expanded
          />
          <b-input
            v-if="isNumber"
            ref="focusedField"
            v-model.number="formValue"
            :autocomplete="$uuid()"
            :disabled="isDisabled"
            :placeholder="noPlaceholder ? '-' : $t('_placeholder.number')"
            rows="4"
            class="has-flex-grow"
            type="number"
            expanded
          />
          <b-select
            v-if="isSelect"
            ref="focusedField"
            v-model="formValue"
            :autocomplete="$uuid()"
            :disabled="isDisabled"
            :placeholder="
              noPlaceholder
                ? '-'
                : $t('_placeholder.custom_field_select', {
                    name: field.field_label.toLowerCase()
                  })
            "
            class="fullwidth-select has-flex-grow"
            expanded
          >
            <option
              v-for="option in field.options"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </b-select>
          <b-checkbox
            v-if="isCheckbox"
            ref="focusedField"
            v-model="formValue"
            :autocomplete="$uuid()"
            :disabled="isDisabled"
            :native-value="1"
            :input-id="`${getFieldName}`"
            :value="false"
            expanded
            class="has-margin-top-50 has-flex-grow"
          >
            <p>
              {{
                `${field.required || isRequiredIf ? "* " : ""}${
                  field.field_label || field.name
                }: `
              }}
              <strong>
                {{ formValue ? $t("_.yes") : $t("_.no") }}
              </strong>
            </p>
          </b-checkbox>
          <div v-if="isRadio">
            <b-radio
              v-for="option in field.options"
              :key="option.value"
              v-model="formValue"
              :input-id="`${getFieldName}_${$_.get(option, 'value')}`"
              :native-value="option.value"
              :disabled="isDisabled"
            >
              {{ option.label }}
            </b-radio>
          </div>
          <b-slider
            v-if="isSlider"
            ref="focusedField"
            v-model="formValue"
            :disabled="isDisabled"
            :min="$_.get(field, 'extra_attributes.min', 0)"
            :max="$_.get(field, 'extra_attributes.max', 100)"
            :step="$_.get(field, 'extra_attributes.step', 1)"
            size="is-medium"
            class="has-flex-grow"
            rounded
          />
          <b-datepicker
            v-if="isDate"
            ref="focusedField"
            v-model="dateFormValue"
            :disabled="isDisabled"
            :placeholder="
              noPlaceholder ? '-' : $t('_placeholder.datestamp_example')
            "
            expanded
            open-on-focus
            class="has-flex-grow"
            @input="onDateChange"
          />
        </template>
      </b-field>
    </u-field>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import _ from "@/boot/lodash";
import HasValidation from "@/mixins/HasValidation";
import BrandMixin from "@/mixins/brandMixin";
import CountriesStore from "@/mixins/constants/countriesStore";
import Context from "@/mixins/context";
import {
  BlueprintFieldsTypes,
  ProvisionFieldTypes
} from "@/data/enums/provisioning";
import { DataModules } from "@/store/modules/data/modules";
import { SemanticTypes } from "@/data/constants";
import { UpmindModuleCodes } from "@/data/enums/upmind";
import type { PropType } from "vue";

import type { IBlueprintField } from "@/models/provisioning";
import { BACKEND_DATE_FORMAT } from "@/data/date";

export default defineComponent({
  name: "ProvisionField",
  components: {
    "smart-domain-field": () =>
      import("@/modules/web_hosting/components/domains/smartDomainField.vue"),
    "phone-number-field": () =>
      import("@/components/app/global/fields/phoneNumberField.vue")
  },
  mixins: [HasValidation, BrandMixin, CountriesStore, Context],
  props: {
    value: { type: null as unknown as PropType<any> },
    field: { type: Object as PropType<IBlueprintField>, required: true },
    fieldValues: {
      type: Object as PropType<IBlueprintField>,
      default: () => ({})
    },
    fieldId: { type: String, required: true },
    isDisabled: { type: Boolean, default: false },
    isReadOnly: { type: Boolean, default: false },
    showLabel: { type: Boolean, default: true },
    type: {
      type: String as PropType<ProvisionFieldTypes>,
      default: ProvisionFieldTypes.PRODUCT_FIELD
    },
    noPlaceholder: { type: Boolean, default: false },
    initialFocuse: { type: Boolean, default: false },
    replaceSemanticTypes: { type: Boolean, default: false }
  },
  data: () => ({
    dataModule: DataModules.PROVISIONING_CATEGORIES,
    isFetching: false,
    data: [] as string[],
    isRequiredIf: false,
    dateFormValue: null as Date | null
  }),
  computed: {
    formValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    brandCountry() {
      return this.countries?.find(
        country => country.id === this.brand?.country_id
      );
    },
    isProvisionAttribute() {
      return this.type === ProvisionFieldTypes.PRODUCT_ATTRIBUTE;
    },
    hasSuggestions() {
      return this.isProvisionAttribute && this.isTextInput;
    },
    getLabel() {
      return (
        this.getFieldName +
        `${this.field.required || this.isRequiredIf ? "*" : ""}${
          this.isSlider ? `: ${this.formValue}` : ""
        }`
      );
    },
    getFieldName() {
      const fieldName = _.get(
        this.field,
        "field_label",
        _.get(this.field, "name", "")
      ).toLowerCase();

      return fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
    },
    rules() {
      const isRequired =
        (this.field.required && this.field.required !== undefined) ||
        this.isRequiredIf;

      return {
        required_phone:
          this.isTelInput && isRequired && !this.isReadOnly
            ? { countryCode: this.brandCountry?.code }
            : false,
        required_number: this.isNumber && isRequired && !this.isReadOnly,
        required: !this.isNumber && isRequired && !this.isReadOnly
      };
    },
    isRequired() {
      return this.field.required;
    },
    isTextInput() {
      return this.field.field_type === BlueprintFieldsTypes.INPUT_TEXT;
    },
    isTelInput() {
      return this.field.field_type === BlueprintFieldsTypes.INPUT_TEL;
    },
    isPasswordInput() {
      return this.field.field_type === BlueprintFieldsTypes.INPUT_PASSWORD;
    },
    isNumber() {
      return this.field.field_type === BlueprintFieldsTypes.INPUT_NUMBER;
    },
    isTextarea() {
      return this.field.field_type === BlueprintFieldsTypes.TEXTAREA;
    },
    isSelect() {
      return this.field.field_type === BlueprintFieldsTypes.SELECT;
    },
    isRadio() {
      return this.field.field_type === BlueprintFieldsTypes.INPUT_RADIO;
    },
    isCheckbox() {
      return this.field.field_type === BlueprintFieldsTypes.CHECKBOX;
    },
    isSlider() {
      return this.field.field_type === BlueprintFieldsTypes.INPUT_RANGE;
    },
    isDate() {
      return this.field.field_type === BlueprintFieldsTypes.INPUT_DATE;
    },
    isDateTime() {
      return this.field.field_type === BlueprintFieldsTypes.INPUT_DATETIME;
    },
    isSmartDomainField() {
      return (
        this.replaceSemanticTypes &&
        this.field.semantic_type === SemanticTypes.DOMAIN_NAMES &&
        this.$store.getters["org/hasModuleEnabled"](
          UpmindModuleCodes.WEB_HOSTING
        )
      );
    },
    getAsyncData() {
      return this.$_.debounce(this.getSuggestions, 500);
    }
  },
  watch: {
    fieldValues: { handler: "onFieldValuesChange", immediate: true, deep: true }
  },
  async created() {
    if (this.isCheckbox && this.value === undefined) {
      this.$emit("input", false);
    }

    if (this.isDate && this.formValue) {
      this.dateFormValue = this.$moment(_.clone(this.formValue)).toDate();
    }

    if (this.initialFocuse) {
      this.$nextTick(() => {
        const field = _.get(this.$refs, "focusedField");
        if (field) {
          field.focus();
        }
      });
    }
  },
  methods: {
    async getSuggestions() {
      if (
        !this.$userCan(
          "list_provision_configuration_attribute_value_suggestions"
        )
      ) {
        return;
      }

      this.isFetching = true;
      try {
        const results = await this.getAttributeValueSuggestions(this.formValue);
        this.data = [];
        _.forEach(results, result => this.data.push(result));
      } catch (error) {
        this.data = [];
        this.$store.dispatch("api/handleError", error);
      }
      this.isFetching = false;
    },
    getAttributeValueSuggestions(value: string) {
      return this.$store.dispatch(
        `data/${this.dataModule}/getAttributeValueSuggestions`,
        {
          id: _.get(this.field, "pivot.category_id"),
          attributeId: _.get(this.field, "pivot.attribute_id"),
          ignoreStored: true,
          params: {
            search: value
          }
        }
      );
    },
    onDateChange(value) {
      this.$set(
        this,
        "formValue",
        this.$moment(value).format(BACKEND_DATE_FORMAT)
      );
    },
    onFieldValuesChange() {
      const requiredRule = this.$_.find(
        this.field.validation_rules,
        validationRule => validationRule.includes("required_")
      );

      if (!requiredRule) {
        this.isRequiredIf = false;
      }

      if (requiredRule) {
        if (requiredRule.includes("required_without_all:")) {
          this.$set(
            this,
            "isRequiredIf",
            this.$_.every(
              this.getMappedRequiredFields(
                requiredRule.replace("required_without_all:", "").split(",")
              )
            )
          );
        }

        if (requiredRule.includes("required_without:")) {
          this.$set(
            this,
            "isRequiredIf",
            this.$_.some(
              this.getMappedRequiredFields(
                requiredRule.replace("required_without:", "").split(",")
              )
            )
          );
        }

        if (requiredRule.includes("required_with_all:")) {
          this.$set(
            this,
            "isRequiredIf",
            this.$_.every(
              this.getMappedRequiredFields(
                requiredRule.replace("required_with_all:", "").split(","),
                false
              )
            )
          );
        }

        if (requiredRule.includes("required_with:")) {
          this.$set(
            this,
            "isRequiredIf",
            this.$_.some(
              this.getMappedRequiredFields(
                requiredRule.replace("required_with:", "").split(","),
                false
              )
            )
          );
        }
      }
    },
    getMappedRequiredFields(requiredFields, empty = true) {
      const emptyValues = ["", undefined, null];

      return _.map(requiredFields, requiredFieldName => {
        const includes = emptyValues.includes(
          this.fieldValues[requiredFieldName]
        );
        return empty ? includes : !includes;
      });
    }
  }
});
</script>

<style lang="scss" scoped>
.has-flex-grow {
  flex-grow: 1;
}
</style>
